import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useBrand } from "@/config/brands";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import { toast as sonnerToast } from "sonner";
import { withScenario } from "@/lib/mapNav";
import { Search as SearchIcon, X as XIcon } from "lucide-react";
import { MapAdminPanel } from "@/components/admin/MapAdminPanel";
import { Helmet } from "react-helmet-async";
import { MatrixLoader } from "@/components/MatrixLoader";
import { useLiveDb } from "@/lib/offlineData";
import { loadScenarioCoverageFor, getCoverageForScenario } from "@/lib/scenarioCoverageStore";
import { loadStaticData } from "@/lib/offlineData";
import { blendCoverageRow } from "@/lib/priceSanity";

// Extracted hooks
import { useHospitalMapData } from "@/hooks/useHospitalMapData";
import { useCesiumViewer } from "@/hooks/useCesiumViewer";
import { useMapMarkers } from "@/hooks/useMapMarkers";
import { useGpsLocation } from "@/hooks/useGpsLocation";
import { useGoogleMap2D } from "@/hooks/useGoogleMap2D";
import { googleMapsFailed, onGoogleMapsFailure } from "@/lib/googleMapsLoader";
import { getMarkerColor } from "@/lib/markerColors";
import { getGrade, getHospitalGrade, gradeColor } from "@/lib/grades";
import { MapFilters, DEFAULT_FILTERS, filtersFromParams, filtersToParams, hospitalPassesFilters, countActiveFilters } from "@/lib/mapFilters";
import { MapFilterRail } from "@/components/map/MapFilterRail";
import { MobileHospitalSheet } from "@/components/map/MobileHospitalSheet";
import OpenStreetMapFallback from "@/components/OpenStreetMapFallback";

import { useTheme } from "@/contexts/ThemeContext";

// Extracted components
import { MapLeftPanel } from "@/components/map/MapLeftPanel";
import { MapRightPanel } from "@/components/map/MapRightPanel";
import { MapTopOverlays } from "@/components/map/MapTopOverlays";
import { MapBottomPanel } from "@/components/map/MapBottomPanel";
import { MapDebugPanel } from "@/components/map/MapDebugPanel";
import { MapModeToggle } from "@/components/map/MapModeToggle";

import type { Hospital, DebugToggles, ScenarioInfo, ScenarioPricing } from "@/types/map";

type MapMode = '2d' | '3d';

const HospitalMap = () => {
  const { stateOrHospital, cityOrHospital, state: stateParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation('map');

  // Parse URL parameters
  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isStateCode = stateOrHospital && /^[A-Z]{2}$/i.test(stateOrHospital);
  const urlState = (stateParam || (isStateCode ? stateOrHospital : null))?.toUpperCase() ?? null;
  const isHospitalId = !!cityOrHospital && UUID_REGEX.test(cityOrHospital);
  // Keep the raw slug too ("winston-salem") — the spaced form loses hyphens,
  // so city matching downstream tries both variants.
  const urlCitySlug = cityOrHospital && !isHospitalId ? cityOrHospital.toLowerCase() : null;
  const urlCity = urlCitySlug
    ? urlCitySlug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : null;
  // Single-segment param must be a real UUID to be treated as a hospital id;
  // anything else (not a state code either) is an unknown map view.
  const singleSegmentIsUuid = !!stateOrHospital && !isStateCode && UUID_REGEX.test(stateOrHospital);
  const hospitalId = isHospitalId ? cityOrHospital : (singleSegmentIsUuid ? stateOrHospital : null);
  const invalidSingleSegment = !!stateOrHospital && !isStateCode && !singleSegmentIsUuid;

  // Brand config
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const ahpHeaderOffset = useAhpHeaderOffset();
  // Tablet gap fix: the side panels are CSS-hidden below lg (1024px), so any
  // viewport narrower than that must get the mobile chrome (FAB + drawer) —
  // NOT the full-page sidebar the old 768px useIsMobile cutoff produced.
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsNarrow(mql.matches);
    mql.addEventListener('change', onChange);
    setIsNarrow(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  // On narrow screens the left panel is a slide-in drawer (closed by default)
  // so the full-screen map is actually visible instead of covered by the panel.
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  // OSM (Leaflet) is THE map — Google was ditched (Master 2026-07-11:
  // "we aren't using google anymore just opensource"). gmFailed stays as the
  // variable name the branches key on, but it's now unconditionally true, so
  // the filter rail, grade markers, legend, and mobile sheet never depend on
  // Google failure detection firing first.
  const [gmFailed, setGmFailed] = useState(true);
  useEffect(() => onGoogleMapsFailure(() => setGmFailed(true)), []);
  const panelBg = isAhp ? "rgba(0, 40, 104, 0.92)" : "rgba(0, 0, 0, 0.85)";
  const panelBgGradient = isAhp ? "linear-gradient(135deg, rgba(0,40,104,0.95), rgba(0,28,72,0.98))" : "linear-gradient(45deg, #000000d1, #000000)";
  const accentColor = isAhp ? "text-[#C5A55A]" : "text-matrix-green";
  const accentBgClass = isAhp ? "bg-[#B22234] hover:bg-[#9a1d2d] text-white" : "bg-matrix-green hover:bg-matrix-green/90 text-black";
  const panelCardBg = isAhp ? "bg-[#001c48]/60 border-[#C5A55A]/20" : "bg-gradient-terminal border-border";
  const logoBorderClass = isAhp ? "" : (brand.key === 'luigis' ? 'neon-border-blink-red' : 'neon-border-blink');

  // ─── Map Mode ──────────────────────────────────────────────────────
  const [mapMode, setMapMode] = useState<MapMode>('2d');
  const [cesiumEverActivated, setCesiumEverActivated] = useState(false);
  const [is3dLoading, setIs3dLoading] = useState(false);

  // Refs
  const cesiumContainerRef = useRef<HTMLDivElement>(null);
  const googleMapContainerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);

  // UI state
  const [selectedTableState, setSelectedTableState] = useState<string>(urlState || 'USA');
  const [selectedTableCity, setSelectedTableCity] = useState<string>(urlCity || '');
  const [showMatrixLoader, setShowMatrixLoader] = useState(!hospitalId);
  const [isProceduresExpanded, setIsProceduresExpanded] = useState(false);
  const [procedureCategory, setProcedureCategory] = useState<string>('common');
  const [priceFilter, setPriceFilter] = useState<'all' | 'gouging'>('all');
  const [hospitalSearch, setHospitalSearch] = useState("");
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showMarkerSettings, setShowMarkerSettings] = useState(false);
  const [isAdminPanelCollapsed, setIsAdminPanelCollapsed] = useState(true);

  // Debug panel
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugToggles, setDebugToggles] = useState<DebugToggles>({
    cesium3DTiles: true, markers: true, labels: true, polylines: true,
    htmlOverlays: false, rightPanel: true, leftPanel: true, topOverlays: true,
    watermark: true, bottomCarousel: true, animations: true, skybox: true, borderImagery: true,
  });
  const debugTogglesRef = useRef(debugToggles);
  debugTogglesRef.current = debugToggles;

  // Scenario mode
  const liveDb = useLiveDb();
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(searchParams.get('scenario'));
  const [scenarioInfo, setScenarioInfo] = useState<ScenarioInfo | null>(null);
  const [scenarioHospitalPricing, setScenarioHospitalPricing] = useState<ScenarioPricing>(new Map());

  // Admin
  const { data: adminStatus } = useAdminStatus();

  // ─── Hooks ─────────────────────────────────────────────────────────
  const hospitalData = useHospitalMapData({ urlState, urlCity, urlCitySlug, hospitalId });
  const {
    hospitals, setHospitals, selectedHospital, setSelectedHospital,
    hospitalStats, hospitalProcedures, hospitalEarnings, isLoadingEarnings,
    comparisonData, isLoading, isDataReady, hasFetchedHospitals, searchCode, setSearchCode,
    fetchHospitals, fetchHospitalStats, fetchHospitalProcedures,
    fetchHospitalEarnings, searchProcedure,
  } = hospitalData;

  // --- Cesium 3D hook (always called, but lazy-inits) ---
  const cesium = useCesiumViewer({ brand, cesiumContainerRef });
  const {
    viewerRef, mapLoaded: cesiumLoaded, isAutoOrbiting, setIsAutoOrbiting,
    isContinuousOrbit, setIsContinuousOrbit, isOrbitingRef,
    inactivityTimer, setInactivityTimer,
    initializeCesium, stopOrbit, orbitHospital: cesiumOrbitHospital,
    startContinuousRotation, flyToState: cesiumFlyToState, flyToCity: cesiumFlyToCityRaw,
    stopAutoTour,
  } = cesium;

  // --- Google Maps 2D hook ---
  const onHospitalClick2D = useCallback((hospital: Hospital) => {
    setShowMatrixLoader(false);
    setSelectedHospital(hospital);
    fetchHospitalProcedures(hospital.id);
    fetchHospitalEarnings(hospital.id);
    navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
  }, [setSelectedHospital, fetchHospitalProcedures, fetchHospitalEarnings, navigate]);

  const googleMap = useGoogleMap2D({
    containerRef: googleMapContainerRef,
    isAhp,
    isDark,
    activeScenarioId,
    scenarioHospitalPricing,
    isDataReady,
    onHospitalClick: onHospitalClick2D,
  });

  // Effective map loaded state
  const mapLoaded = mapMode === '2d' ? googleMap.mapLoaded : cesiumLoaded;

  // ─── OSM map: grade/rank marker data ────────────────────────────────
  // Scenario mode ranks hospitals by blended scenario price (1 = cheapest);
  // rankings mode uses the national overall_rank_gross from the DB.
  const scenarioRanks = useMemo(() => {
    const m = new Map<string, number>();
    if (!activeScenarioId) return m;
    [...scenarioHospitalPricing.entries()]
      .sort((a, b) => a[1].blendedPrice - b[1].blendedPrice)
      .forEach(([id], i) => m.set(id, i + 1));
    return m;
  }, [activeScenarioId, scenarioHospitalPricing]);

  // National universe for "#rank of total" — a state view only loads that
  // state's hospitals, so max(rank) over the loaded set undercounts. One cheap
  // HEAD count, cached for the session; falls back to max(rank) if it fails.
  const { data: nationalRankedCount } = useQuery({
    queryKey: ['ranked-hospital-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('hospital_ranking_summary')
        .select('*', { count: 'exact', head: true })
        .not('overall_rank_gross', 'is', null);
      return count ?? null;
    },
    staleTime: Infinity,
  });

  const osmTotalRanked = useMemo(() => {
    if (activeScenarioId) return scenarioRanks.size || null;
    if (nationalRankedCount) return nationalRankedCount;
    let max = 0;
    hospitals.forEach(h => { if (h.overall_rank_gross && h.overall_rank_gross > max) max = h.overall_rank_gross; });
    return max || null;
  }, [activeScenarioId, scenarioRanks, nationalRankedCount, hospitals]);

  // ─── Consumer filters ───────────────────────────────────────────────
  // Client-side over the loaded set; state round-trips through f_* URL params
  // so filtered views are shareable. Filtered-out hospitals dim, not vanish.
  const [mapFilters, setMapFilters] = useState<MapFilters>(() => filtersFromParams(searchParams));
  const applyFilters = useCallback((f: MapFilters) => {
    setMapFilters(f);
    setSearchParams(prev => filtersToParams(f, prev), { replace: true });
  }, [setSearchParams]);
  const activeFilterCount = countActiveFilters(mapFilters);

  // A–F legend for the OSM map. Same boundaries as /hospital-rankings; the
  // labels read as "% of the national average price" (markup −70% ⇒ 30%).
  const osmLegend = useMemo(() => [
    { color: gradeColor('A'), label: t('map:legend.gradeA', { defaultValue: 'A · ≤30% of natl avg' }) },
    { color: gradeColor('B'), label: t('map:legend.gradeB', { defaultValue: 'B · 30–65%' }) },
    { color: gradeColor('C'), label: t('map:legend.gradeC', { defaultValue: 'C · 65–95%' }) },
    { color: gradeColor('D'), label: t('map:legend.gradeD', { defaultValue: 'D · 95–130%' }) },
    { color: gradeColor('F'), label: t('map:legend.gradeF', { defaultValue: 'F · >130% of natl avg' }) },
  ], [t]);

  // ─── Adapter Functions (dispatch to current mode) ──────────────────

  const flyToState = useCallback((stateCode: string) => {
    if (mapMode === '2d') {
      googleMap.flyToState(stateCode);
    } else {
      cesiumFlyToState(stateCode);
    }
  }, [mapMode, googleMap.flyToState, cesiumFlyToState]);

  const flyToCity = useCallback((city: string, stateCode: string) => {
    if (mapMode === '2d') {
      googleMap.flyToCity(city, stateCode, hospitals);
    } else {
      cesiumFlyToCityRaw(city, stateCode, hospitals);
    }
  }, [mapMode, googleMap.flyToCity, cesiumFlyToCityRaw, hospitals]);

  const focusHospital = useCallback((hospital: Hospital) => {
    if (mapMode === '2d') {
      googleMap.panToHospital(hospital);
    } else {
      cesiumOrbitHospital(hospital);
    }
  }, [mapMode, googleMap.panToHospital, cesiumOrbitHospital]);

  // GPS flyTo callback - works with either map mode
  const flyToLocationForGps = useCallback((lat: number, lng: number) => {
    if (mapMode === '2d') {
      googleMap.flyToLocation(lat, lng);
    } else {
      const Cesium = (window as any).Cesium;
      if (viewerRef.current && Cesium) {
        viewerRef.current.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(lng, lat, 50000),
          orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-45), roll: 0.0 },
          duration: 2.0,
        });
      }
    }
  }, [mapMode, googleMap.flyToLocation, viewerRef]);

  // --- 3D-only markers hook (only active in 3D mode) ---
  const markers = useMapMarkers({
    viewerRef, overlayContainerRef, isDataReady, isOrbitingRef,
    selectedHospital, debugTogglesRef, activeScenarioId,
    scenarioHospitalPricing, hospitalId: hospitalId || null, urlState, urlCity,
    isAhp, setSelectedHospital, fetchHospitalProcedures, orbitHospital: cesiumOrbitHospital, navigate,
  });

  const gps = useGpsLocation({
    hospitals, flyToLocation: flyToLocationForGps,
    setSelectedTableState, setSelectedTableCity, stopOrbit,
  });

  // One marker-data pass: grade + rank + scenario price + summary-card fields
  // + dimmed flag. Feeds both the OSM map and the "showing X of Y" count.
  // (Lives below useMapMarkers — it reads markers.maxMarkers.)
  const osmHospitals = useMemo(() => {
    return hospitals.slice(0, markers.maxMarkers).filter(h => h.latitude && h.longitude).map(h => {
      const pricing = activeScenarioId ? scenarioHospitalPricing.get(h.id) : undefined;
      const grade = activeScenarioId
        ? (pricing ? getGrade(pricing.blendedMarkup) : null)
        : (h.overall_rank_gross != null ? getHospitalGrade(h.median_markup_percentage ?? h.avg_markup_percentage ?? 0) : null);
      const rank = activeScenarioId
        ? (scenarioRanks.get(h.id) ?? null)
        : (h.overall_rank_gross ?? null);
      const scenarioPrice = pricing ? Math.round(pricing.blendedPrice) : null;
      return {
        hospital_id: h.id, hospital_name: h.name, latitude: h.latitude, longitude: h.longitude,
        city: h.city, state: h.state,
        grade, rank, scenarioPrice,
        cmsStars: h.cms_overall_rating ?? null,
        cmsSafety: h.cms_safety_rating ?? null,
        cmsMortality: h.cms_mortality_rating ?? null,
        cmsReadmission: h.cms_readmission_rating ?? null,
        cmsExperience: h.cms_patient_experience_rating ?? null,
        googleRating: h.google_rating ?? null,
        googleReviews: h.google_review_count ?? null,
        ownership: h.ownership ?? null,
        hospitalType: h.hospital_type ?? null,
        bedCount: h.bed_count ?? null,
        emergencyServices: h.emergency_services ?? null,
        hospitalSystem: h.hospital_system ?? null,
        dimmed: activeFilterCount > 0 && !hospitalPassesFilters(h, mapFilters, { grade, scenarioPrice }),
        color: getMarkerColor({
          outlierPercentage: h.outlier_percentage ?? 0, dataReady: true, followed: h.followed,
          totalProcedures: h.total_procedures, totalChargemasterFiles: h.total_chargemaster_files ?? 0,
          hospitalId: h.id, activeScenarioId, scenarioHospitalPricing,
        }),
      };
    });
  }, [hospitals, markers.maxMarkers, activeScenarioId, scenarioHospitalPricing, scenarioRanks, mapFilters, activeFilterCount]);
  const visibleCount = useMemo(() => osmHospitals.filter(h => !h.dimmed).length, [osmHospitals]);

  // Filtered hospitals for search
  const filteredHospitals = hospitals.filter(hospital =>
    hospital.name.toLowerCase().includes(hospitalSearch.toLowerCase()) ||
    hospital.city.toLowerCase().includes(hospitalSearch.toLowerCase()) ||
    hospital.address.toLowerCase().includes(hospitalSearch.toLowerCase())
  ).slice(0, 10);

  const handleHospitalSelect = (hospital: Hospital) => {
    setShowMatrixLoader(false);
    setSelectedHospital(hospital);
    setHospitalSearch("");
    setShowHospitalDropdown(false);
    fetchHospitalProcedures(hospital.id);
    fetchHospitalEarnings(hospital.id);
    setTimeout(() => focusHospital(hospital), 100);
    navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
  };

  const addHospitalMarkersWrapper = (viewer: any) => {
    markers.addHospitalMarkers(viewer, hospitals);
  };

  // ─── Map Mode Switching ────────────────────────────────────────────

  const handleMapModeChange = useCallback(async (newMode: MapMode) => {
    if (newMode === mapMode) return;

    if (newMode === '3d' && !cesiumEverActivated) {
      setIs3dLoading(true);
      setCesiumEverActivated(true);
      setMapMode('3d');
      // Cesium will initialize via the effect below
    } else {
      setMapMode(newMode);
    }
  }, [mapMode, cesiumEverActivated]);

  // ─── Effects ───────────────────────────────────────────────────────

  // Lock horizontal scroll on mount (prevent page-level side-scroll while using map)
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    (window as any).hospitalMap = {
      selectHospital: (hId: string) => {
        const hospital = hospitals.find(h => h.id === hId);
        if (hospital) handleHospitalSelect(hospital);
      }
    };
    return () => {
      document.body.style.overflowX = '';
      document.documentElement.style.overflowX = '';
      delete (window as any).hospitalMap;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update window function when hospitals change
  useEffect(() => {
    if ((window as any).hospitalMap) {
      (window as any).hospitalMap.selectHospital = (hId: string) => {
        const hospital = hospitals.find(h => h.id === hId);
        if (hospital) handleHospitalSelect(hospital);
      };
    }
  }, [hospitals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync UI state when route changes. The hospitals fetch itself is owned by
  // react-query inside useHospitalMapData (keyed by state/city), so a
  // hospitalId-only change (marker click within the same state) no longer
  // triggers a refetch — which is what used to close the just-opened popup.
  useEffect(() => {
    if (!hospitalId) setSelectedHospital(null);
    setSelectedTableState(urlState || 'USA');
    setSelectedTableCity(urlCity || '');
  }, [urlState, urlCity, hospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // DB stats are route-independent — fetch once on mount.
  useEffect(() => {
    fetchHospitalStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Bad URL param: single segment that's neither a state code nor a UUID.
  useEffect(() => {
    if (!invalidSingleSegment) return;
    sonnerToast("We couldn't find that map view", { description: "Showing the national map instead." });
    navigate('/map', { replace: true });
  }, [invalidSingleSegment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Valid-but-unknown hospital UUID: fall back to the state (or national) view.
  useEffect(() => {
    if (!hospitalId || !hasFetchedHospitals || hospitals.length === 0) return;
    if (!hospitals.find(h => h.id === hospitalId)) {
      sonnerToast("We couldn't find that hospital", { description: "Showing the surrounding area instead." });
      navigate(withScenario(urlState ? `/map/${urlState.toLowerCase()}` : '/map'), { replace: true });
    }
  }, [hospitalId, hasFetchedHospitals, hospitals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize 2D Google Map on mount
  useEffect(() => {
    if (googleMapContainerRef.current && !googleMap.mapLoaded) {
      try {
        const initPromise = googleMap.initialize();
        // initialize() is async — a rejection (script blocked, bad key, Maps
        // constructor throwing) flips the open-source fallback.
        if (initPromise && typeof (initPromise as Promise<void>).catch === 'function') {
          (initPromise as Promise<void>).catch(() => setGmFailed(true));
        }
      } catch {
        setGmFailed(true);
      }
    }
  }, [googleMap.mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Cesium ONLY when first switching to 3D
  useEffect(() => {
    if (mapMode === '3d' && cesiumEverActivated && cesiumContainerRef.current && !cesiumLoaded) {
      initializeCesium();
    }
  }, [mapMode, cesiumEverActivated, cesiumLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark 3D loading done when Cesium finishes
  useEffect(() => {
    if (cesiumLoaded && is3dLoading) setIs3dLoading(false);
  }, [cesiumLoaded, is3dLoading]);

  // Hide loader when data ready
  useEffect(() => {
    if (isDataReady && showMatrixLoader) setShowMatrixLoader(false);
    if (showMatrixLoader) {
      const t = setTimeout(() => setShowMatrixLoader(false), 1500);
      return () => clearTimeout(t);
    }
  }, [isDataReady, showMatrixLoader]);

  // Start cinematic animation (3D only)
  useEffect(() => {
    if (mapMode !== '3d') return;
    if (!showMatrixLoader && isDataReady && viewerRef.current && !selectedHospital && !hospitalId) {
      const Cesium = (window as any).Cesium;
      if (!Cesium || !viewerRef.current?.camera) return;
      const endPosition = {
        longitude: -88.83271267118157, latitude: 31.26835010454,
        height: 433407.99506119936, heading: 279.0516601140881,
        pitch: -29.97841737518171, roll: 0.00
      };
      setTimeout(() => {
        if (!viewerRef.current?.camera || !viewerRef.current?.scene) return;
        const destination = Cesium.Cartesian3.fromDegrees(endPosition.longitude, endPosition.latitude, endPosition.height);
        viewerRef.current.camera.flyTo({
          destination,
          orientation: {
            heading: Cesium.Math.toRadians(endPosition.heading),
            pitch: Cesium.Math.toRadians(endPosition.pitch),
            roll: endPosition.roll
          },
          duration: 4.0,
          complete: () => {
            startContinuousRotation(destination, {
              heading: Cesium.Math.toRadians(endPosition.heading),
              pitch: Cesium.Math.toRadians(endPosition.pitch),
              roll: endPosition.roll
            });
          }
        });
      }, 500);
    }
  }, [mapMode, showMatrixLoader, isDataReady, selectedHospital, hospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add 2D markers when data loaded and in 2D mode
  useEffect(() => {
    if (mapMode === '2d' && googleMap.mapLoaded && hospitals.length > 0) {
      googleMap.addHospitalMarkers(hospitals, markers.maxMarkers);
    }
  }, [mapMode, googleMap.mapLoaded, hospitals, isDataReady, scenarioHospitalPricing, markers.maxMarkers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add 3D markers when data loaded and in 3D mode
  useEffect(() => {
    if (mapMode === '3d' && cesiumLoaded && hospitals.length > 0 && viewerRef.current && overlayContainerRef.current) {
      addHospitalMarkersWrapper(viewerRef.current);
    }
  }, [mapMode, cesiumLoaded, hospitals, isDataReady, scenarioHospitalPricing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to city/state from URL. If the city fetch came back empty (typo,
  // punctuation mismatch, no data), tell the user and fly to the state instead.
  const cityNotFoundToastRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapLoaded || !urlCity || !urlState) return;
    if (hospitals.length > 0) {
      flyToCity(urlCity, urlState);
    } else if (hasFetchedHospitals) {
      if (cityNotFoundToastRef.current !== urlCity) {
        cityNotFoundToastRef.current = urlCity;
        sonnerToast(`No hospitals found in ${urlCity}`, { description: "Showing the whole state instead." });
      }
      flyToState(urlState);
    }
  }, [mapLoaded, hospitals, urlCity, urlState, hasFetchedHospitals]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapLoaded && urlState && !urlCity && !hospitalId) flyToState(urlState);
  }, [mapLoaded, urlState, urlCity, hospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hospital-specific initialization — zoom + select when loading from URL with hospitalId
  useEffect(() => {
    if (!hospitalId || hospitals.length === 0 || !mapLoaded || showMatrixLoader) return;
    const targetHospital = hospitals.find(h => h.id === hospitalId);
    if (targetHospital && !selectedHospital) {
      setSelectedHospital(targetHospital);
      fetchHospitalProcedures(targetHospital.id);
      fetchHospitalEarnings(targetHospital.id);
      setTimeout(() => focusHospital(targetHospital), 500);
    }
  }, [hospitals, hospitalId, showMatrixLoader, selectedHospital, mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scenario sync
  useEffect(() => {
    const scenarioParam = searchParams.get('scenario');
    if (scenarioParam !== activeScenarioId) setActiveScenarioId(scenarioParam);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeScenarioId) {
      setScenarioInfo(null);
      setScenarioHospitalPricing(new Map());
      return;
    }
    const loadScenarioData = async () => {
      try {
        // Try static cache first (unless ?db=live)
        if (!liveDb) {
          const loaded = await loadScenarioCoverageFor(activeScenarioId);
          if (loaded) {
            const rows = getCoverageForScenario(activeScenarioId);
            if (rows.length > 0) {
              // Get scenario name + national total from static scenarios.json
              const staticScenarios = await loadStaticData<{ data: Array<{ id: string; name: string; estimatedCost: number; totalProcedures: number }> }>('scenarios.json');
              const scenarioData = staticScenarios?.data?.find((s: any) => s.id === activeScenarioId);
              const nationalTotal = scenarioData ? Math.round(Number(scenarioData.estimatedCost)) : 0;
              const scenarioName = scenarioData?.name || 'Scenario';
              const totalCodes = scenarioData?.totalProcedures || 0;

              setScenarioInfo({ name: scenarioName, nationalAvgPrice: nationalTotal, totalCodes, hospitalCount: rows.length });
              const pricingMap = new Map<string, { totalPrice: number; avgMarkup: number; coverage: number; blendedPrice: number; blendedMarkup: number }>();
              rows.forEach(c => {
                const blended = blendCoverageRow({ hp: c.hp, np: c.np, mc: c.mc, tc: c.tc, pm: c.pm, nationalCost: nationalTotal });
                if (!blended) return; // garbage/unpriceable row
                pricingMap.set(c.h, { totalPrice: c.hp, avgMarkup: c.mp, coverage: c.cp, blendedPrice: blended.blendedPrice, blendedMarkup: blended.blendedMarkup ?? c.mp });
              });
              setScenarioHospitalPricing(pricingMap);
              return;
            }
          }
        }

        // Supabase fallback (or ?db=live)
        const { data: scenario, error: scenarioErr } = await supabase.from('scenarios').select('name, total_procedures').eq('id', activeScenarioId).single();
        if (scenarioErr || !scenario) { console.error('Failed to load scenario:', scenarioErr); return; }
        const { data: coverage, error: coverageErr } = await supabase.from('scenario_hospital_coverage')
          .select('hospital_id, total_hospital_price, total_national_price, avg_markup_percentage, coverage_percentage, matched_codes, total_codes, pricing_method')
          .eq('scenario_id', activeScenarioId)
          // Parity with the static shards + profile page: cornerstone-passing rows only.
          .eq('cornerstone_pass', true)
          .gte('coverage_percentage', 50).gt('total_hospital_price', 0)
          .limit(5000);
        if (coverageErr || !coverage) { console.error('Failed to load coverage:', coverageErr); return; }
        const { data: cache } = await supabase.from('scenario_costs_cache').select('real_total_cost').eq('scenario_id', activeScenarioId).single();
        const nationalTotal = cache?.real_total_cost ? Math.round(Number(cache.real_total_cost)) : 0;
        setScenarioInfo({ name: scenario.name, nationalAvgPrice: nationalTotal, totalCodes: scenario.total_procedures || 0, hospitalCount: coverage.length });
        const pricingMap = new Map<string, { totalPrice: number; avgMarkup: number; coverage: number; blendedPrice: number; blendedMarkup: number }>();
        coverage.forEach((c: any) => {
          const tp = Number(c.total_hospital_price);
          const blended = blendCoverageRow({ hp: tp, np: c.total_national_price, mc: c.matched_codes, tc: c.total_codes, pm: c.pricing_method, nationalCost: nationalTotal });
          if (!blended) return; // garbage/unpriceable row
          pricingMap.set(c.hospital_id, { totalPrice: tp, avgMarkup: Number(c.avg_markup_percentage), coverage: Number(c.coverage_percentage), blendedPrice: blended.blendedPrice, blendedMarkup: blended.blendedMarkup ?? Number(c.avg_markup_percentage) });
        });
        setScenarioHospitalPricing(pricingMap);
      } catch (err) {
        console.error('Scenario data load failed:', err);
      }
    };
    loadScenarioData();
  }, [activeScenarioId, liveDb]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug panel keyboard shortcut — dev builds only
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const handleDebugKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') { e.preventDefault(); setShowDebugPanel(prev => !prev); }
    };
    document.addEventListener('keydown', handleDebugKey);
    return () => document.removeEventListener('keydown', handleDebugKey);
  }, []);

  // Apply debug toggles to Cesium viewer (3D only)
  useEffect(() => {
    if (mapMode !== '3d' || !viewerRef.current) return;
    const viewer = viewerRef.current;
    if (viewer.scene?.primitives) {
      for (let i = 0; i < viewer.scene.primitives.length; i++) {
        const prim = viewer.scene.primitives.get(i);
        if (prim?.constructor?.name === 'Cesium3DTileset') prim.show = debugToggles.cesium3DTiles;
      }
    }
    viewer.entities?.values?.forEach((entity: any) => {
      if (entity.billboard) entity.billboard.show = debugToggles.markers;
      if (entity.label && !entity.isStatsLabel) entity.label.show = debugToggles.labels;
      if (entity.polyline) entity.polyline.show = debugToggles.polylines;
    });
    if (overlayContainerRef.current) overlayContainerRef.current.style.display = debugToggles.htmlOverlays ? '' : 'none';
    if (viewer.scene?.skyBox) viewer.scene.skyBox.show = debugToggles.skybox;
    if ((viewer as any)._borderPrimitive) {
      (viewer as any)._borderPrimitive.show = debugToggles.borderImagery;
    }
    if (viewer.scene) viewer.scene.requestRenderMode = !debugToggles.animations;
    viewer.scene?.requestRender();
  }, [debugToggles, cesiumLoaded, mapMode]);

  // Handle hospital selection — stop continuous orbit (3D only)
  useEffect(() => {
    if (selectedHospital) {
      if (isContinuousOrbit) { setIsContinuousOrbit(false); stopOrbit(); }
    } else {
      setIsAutoOrbiting(false);
    }
  }, [selectedHospital, isContinuousOrbit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.hospital-search-container')) setShowHospitalDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Render ────────────────────────────────────────────────────────
  // Canonical reflects the actual view (/map, /map/tx, /map/tx/houston, ...)
  // instead of always pointing at /map.
  const canonicalPath = hospitalId
    ? (urlState ? `/map/${urlState.toLowerCase()}/${hospitalId}` : '/map')
    : urlState
      ? (urlCitySlug ? `/map/${urlState.toLowerCase()}/${urlCitySlug}` : `/map/${urlState.toLowerCase()}`)
      : '/map';
  const canonicalUrl = `${brand.primaryDomain}${canonicalPath}${activeScenarioId ? `?scenario=${activeScenarioId}` : ''}`;
  const baseTitle = scenarioInfo
    ? t('meta.scenarioPageTitle', { scenarioName: scenarioInfo.name, brandName: brand.name })
    : t('meta.pageTitle', { brandName: brand.name });
  const pageTitle = urlState ? `${baseTitle} — ${urlState}` : baseTitle;

  return (
    <div className="w-screen bg-background overflow-hidden z-1" style={{ position: 'relative', height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : '100vh', userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' as any }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={scenarioInfo
          ? t('meta.scenarioDescription', { scenarioName: scenarioInfo.name })
          : t('meta.description')
        } />
        <meta name="keywords" content={scenarioInfo
          ? `${scenarioInfo.name} hospital prices, ${scenarioInfo.name} cost by hospital, hospital price map, healthcare cost comparison`
          : "hospital price map, hospital prices by location, hospital costs near me, interactive hospital map, healthcare price transparency map"
        } />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={scenarioInfo ? `${scenarioInfo.name} — Hospital Price Map` : "Hospital Price Map"} />
        <meta property="og:description" content={scenarioInfo
          ? `See which hospitals charge the most for ${scenarioInfo.name}. Interactive map across 9,500+ US hospitals.`
          : "Interactive map of hospital prices across America. Explore chargemaster data for 9,500+ hospitals by location."
        } />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={scenarioInfo ? `${scenarioInfo.name} — Hospital Price Map` : "Hospital Price Map"} />
        <meta name="twitter:description" content={scenarioInfo
          ? `See which hospitals charge the most for ${scenarioInfo.name} across the US.`
          : "Interactive map of hospital prices across America. Explore chargemaster data for 9,500+ hospitals."
        } />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>
      <div className="w-screen bg-background flex" style={{ height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : '100vh', userSelect: 'none', WebkitUserSelect: 'none' }}>

        {/* Left Panel — desktop sidebar; on mobile a slide-in drawer over the
            full-screen map (closed by default so the map is actually visible). */}
        {(() => {
          const leftPanel = (
            <MapLeftPanel
              brand={brand} isAhp={isAhp} panelBg={panelBg} accentBgClass={accentBgClass}
              logoBorderClass={logoBorderClass} debugToggles={debugToggles}
              flyToState={flyToState} navigate={navigate}
              setSelectedTableState={setSelectedTableState} setSelectedTableCity={setSelectedTableCity}
              setSelectedHospital={(h: any) => { setSelectedHospital(h); if (isNarrow) setMobilePanelOpen(false); }}
              stateSearch={stateSearch} setStateSearch={setStateSearch}
              showStateDropdown={showStateDropdown} setShowStateDropdown={setShowStateDropdown}
              findNearMe={gps.findNearMe} isLocatingGps={gps.isLocatingGps}
              nearbyHospitals={gps.nearbyHospitals} userLocation={gps.userLocation}
              showEROnly={gps.showEROnly} setShowEROnly={gps.setShowEROnly}
              showUrgentCareOnly={gps.showUrgentCareOnly} setShowUrgentCareOnly={gps.setShowUrgentCareOnly}
              sortByMarkup={gps.sortByMarkup} setSortByMarkup={gps.setSortByMarkup}
              fetchHospitalProcedures={fetchHospitalProcedures} orbitHospital={focusHospital}
              activeScenarioId={activeScenarioId} setActiveScenarioId={setActiveScenarioId}
              scenarioHospitalPricing={scenarioHospitalPricing} setSearchParams={setSearchParams}
            />
          );
          if (!isNarrow) return leftPanel;
          if (!mobilePanelOpen) return null;
          return (
            <div className="fixed inset-0 z-[300]">
              <div className="absolute inset-0 bg-black/50" onClick={() => setMobilePanelOpen(false)} />
              {/* Full-width sheet on mobile so the panel's navy bg fills the screen.
                  The sheet starts BELOW the fixed header: the panel's own height is
                  calc(100vh − headerOffset), so anchoring at the offset makes its
                  navy background land exactly on the bottom of the viewport —
                  starting at top-0 overlapped the header AND left a gap above the
                  footer bar. */}
              <div className="absolute left-0 bottom-0 w-full flex shadow-2xl" style={{ top: ahpHeaderOffset }}>
                {leftPanel}
                <button onClick={() => setMobilePanelOpen(false)} aria-label="Close" className="absolute z-[310] p-2 rounded-full" style={{ top: 8, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', color: '#fff' }}>
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        })()}

        {/* ── Mobile/tablet map chrome (below lg) ─────────────────────────── */}
        {isNarrow && !mobilePanelOpen && (
          <>
            {/* Search FAB — top-left, compact. Icon-only when the scenario
                chip shares the top row (both don't fit at 390px). */}
            <button
              onClick={() => setMobilePanelOpen(true)}
              aria-label={t('mobile.searchFab', { defaultValue: 'Search & scenarios' })}
              className="fixed z-[200] flex items-center gap-2 rounded-full shadow-lg font-semibold"
              style={{ top: `calc(${ahpHeaderOffset}px + 12px)`, left: 12, padding: activeScenarioId ? '10px' : '10px 16px', backgroundColor: ahpDark ? '#0f1d2f' : (isAhp ? '#002868' : '#000'), color: '#fff', fontSize: 14, border: ahpDark ? '1px solid rgba(197,165,90,0.3)' : 'none' }}
            >
              <SearchIcon className="w-4 h-4" /> {!activeScenarioId && t('mobile.searchFab', { defaultValue: 'Search & scenarios' })}
            </button>

            {/* Compact scenario chip — top-right, clears the FAB; tap × to exit */}
            {activeScenarioId && scenarioInfo && (
              <div
                className="fixed z-[200] flex items-center gap-2 px-3 py-2 rounded-full shadow-lg max-w-[58%]"
                style={{ top: `calc(${ahpHeaderOffset}px + 12px)`, right: 12, backgroundColor: ahpDark ? 'rgba(15,29,47,0.95)' : 'rgba(0,28,72,0.95)', border: '1px solid rgba(197,165,90,0.35)', backdropFilter: 'blur(8px)' }}
              >
                <div className="min-w-0">
                  <div className="truncate" style={{ fontSize: 12, fontWeight: 600, color: '#fff', fontFamily: "'Instrument Serif', Georgia, serif" }}>{scenarioInfo.name}</div>
                  <div className="truncate" style={{ fontSize: 10, color: '#C5A55A' }}>~${scenarioInfo.nationalAvgPrice.toLocaleString()} avg · {scenarioInfo.hospitalCount.toLocaleString()} hospitals</div>
                </div>
                <button onClick={() => { setActiveScenarioId(null); setSearchParams({}); }} aria-label="Exit scenario" className="flex-shrink-0 text-white/70 hover:text-white" style={{ fontSize: 16, lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* Price legend — bottom-left, above the tab bar. Hidden when a
                hospital is selected (the bottom detail panel takes that space). */}
            {!selectedHospital && (
            <div
              className="fixed z-[150] rounded-lg px-3 py-2 shadow-lg"
              style={{ bottom: `calc(96px + env(safe-area-inset-bottom, 0px))`, left: 12, backgroundColor: ahpDark ? 'rgba(15,29,47,0.92)' : 'rgba(255,255,255,0.94)', border: `1px solid ${ahpDark ? 'rgba(197,165,90,0.25)' : 'rgba(27,27,27,0.12)'}`, backdropFilter: 'blur(8px)' }}
            >
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: ahpDark ? 'rgba(240,235,227,0.6)' : 'rgba(27,27,27,0.62)', marginBottom: 4 }}>
                {activeScenarioId ? 'This scenario · vs natl avg' : 'Grade · vs natl avg'}
              </div>
              <div className="flex flex-col gap-1">
                {osmLegend.map((it) => (
                  <div key={it.label} className="flex items-center gap-1.5">
                    <span style={{ width: 9, height: 9, borderRadius: 9, backgroundColor: it.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, color: ahpDark ? '#f0ebe3' : '#1B1B1B' }}>{it.label}</span>
                  </div>
                ))}
              </div>
            </div>
            )}
          </>
        )}

        {/* Map Container */}
        <div className="flex-1 relative flex flex-col" style={{ height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : '100vh', width: '100vw', position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
          <div className="flex-1 relative" style={{ height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : '100vh', width: '100vw', userSelect: 'none', WebkitUserSelect: 'none' }}>
            {/* 2D Google Maps container */}
            <div
              ref={googleMapContainerRef}
              className="w-full h-full"
              style={{
                display: mapMode === '2d' ? 'block' : 'none',
                userSelect: 'none', WebkitUserSelect: 'none', cursor: 'default',
              }}
            />

            {/* Open-source fallback — covers the whole map area when Google Maps
                auth fails (bad key / referrer / billing). Both 2D and 3D depend on
                the Google key, so this replaces either. No API key required. */}
            {gmFailed && (
              <div className="absolute inset-0 z-[80]">
                <OpenStreetMapFallback
                  hospitals={osmHospitals}
                  dark={ahpDark}
                  // Legend lives outside the map: MapTopOverlays (desktop) /
                  // the fixed bottom-left panel (mobile) — both grade-aware.
                  legend={[]}
                  totalRanked={osmTotalRanked}
                  selectedId={selectedHospital?.id ?? null}
                  disablePopups={isNarrow}
                  onBackgroundClick={() => setSelectedHospital(null)}
                  focusHospital={
                    selectedHospital && selectedHospital.latitude && selectedHospital.longitude
                      ? { latitude: selectedHospital.latitude, longitude: selectedHospital.longitude }
                      : null
                  }
                  focusState={urlState}
                  onSelect={(id) => { const h = hospitals.find(x => x.id === id); if (h) setSelectedHospital(h); }}
                />
              </div>
            )}

            {/* 3D Cesium container (only rendered after first activation) */}
            {cesiumEverActivated && (
              <div
                ref={cesiumContainerRef}
                id="cesiumContainer"
                className="w-full h-full"
                style={{
                  display: mapMode === '3d' ? 'block' : 'none',
                  userSelect: 'none', WebkitUserSelect: 'none', cursor: 'default',
                }}
              />
            )}

            {/* Mobile summary sheet — narrow screens have no right/bottom
                panel and Leaflet popups are unreadable at thumb scale, so a
                tapped marker opens this instead (popups disabled on narrow). */}
            {gmFailed && isNarrow && selectedHospital && (() => {
              const sheetData = osmHospitals.find(o => o.hospital_id === selectedHospital.id);
              return sheetData ? (
                <MobileHospitalSheet
                  hospital={sheetData}
                  totalRanked={osmTotalRanked}
                  scenarioMode={!!activeScenarioId}
                  dark={ahpDark}
                  onClose={() => setSelectedHospital(null)}
                  bottomOffset={88}
                />
              ) : null;
            })()}

            {/* Consumer filters — five facets over the loaded markers; dims
                non-matching hospitals. OSM map only (it owns the grade badges). */}
            {gmFailed && (
              <MapFilterRail
                filters={mapFilters}
                onChange={applyFilters}
                visibleCount={visibleCount}
                totalCount={osmHospitals.length}
                dark={ahpDark}
                isNarrow={isNarrow}
                topOffset={12}
                rightOffset={debugToggles.rightPanel ? 622 : 12}
              />
            )}

            {/* 2D/3D Toggle — hidden when the OSM map drives (both Google 2D and
                Cesium's Google-3D-tiles need the ditched key; toggle is dead). */}
            {!gmFailed && (
              <MapModeToggle
                mode={mapMode}
                onModeChange={handleMapModeChange}
                isAhp={isAhp}
                is3dLoading={is3dLoading}
              />
            )}

            {/* Stop Orbit Button (3D only) */}
            {mapMode === '3d' && (isOrbitingRef.current || isAutoOrbiting || isContinuousOrbit) && (
              <Button
                variant="outline" size="sm"
                onClick={() => {
                  stopOrbit(); setSelectedHospital(null); setIsAutoOrbiting(false); setIsContinuousOrbit(false);
                  if (inactivityTimer) { clearTimeout(inactivityTimer); setInactivityTimer(null); }
                }}
                className="absolute bottom-6 left-6 z-[100] bg-red-500/90 border-red-600 text-white hover:bg-red-600 shadow-lg"
              >
                {t('navigation.stopOrbit')}
              </Button>
            )}

            {/* HTML Overlays Container (3D only) */}
            {mapMode === '3d' && (
              <div
                ref={overlayContainerRef}
                className={`absolute inset-0 pointer-events-none transition-all duration-300 ${isProceduresExpanded ? 'z-[40]' : 'z-20'}`}
                style={{ overflow: 'visible' }}
              />
            )}

            {/* Top Overlays */}
            <MapTopOverlays
              hospitalStats={hospitalStats} debugToggles={debugToggles}
              osmActive={gmFailed}
              isProceduresExpanded={isProceduresExpanded}
              activeScenarioId={activeScenarioId} setActiveScenarioId={setActiveScenarioId}
              scenarioInfo={scenarioInfo} setSearchParams={setSearchParams}
              hospitalSearch={hospitalSearch} setHospitalSearch={setHospitalSearch}
              showHospitalDropdown={showHospitalDropdown} setShowHospitalDropdown={setShowHospitalDropdown}
              filteredHospitals={filteredHospitals}
              setSelectedHospital={setSelectedHospital} fetchHospitalProcedures={fetchHospitalProcedures}
              orbitHospital={focusHospital} flyToState={flyToState} navigate={navigate}
              setSelectedTableState={setSelectedTableState} setSelectedTableCity={setSelectedTableCity}
              maxMarkers={markers.maxMarkers} setMaxMarkers={markers.setMaxMarkers}
              markerSortOrder={markers.markerSortOrder} setMarkerSortOrder={markers.setMarkerSortOrder}
              showMarkerSettings={showMarkerSettings} setShowMarkerSettings={setShowMarkerSettings}
              hospitals={hospitals} viewerRef={viewerRef}
              addHospitalMarkers={addHospitalMarkersWrapper} toast={toast} isAhp={isAhp}
            />

            {/* Admin Panel */}
            {adminStatus?.isAdmin && selectedHospital && (
              <div className={`absolute top-4 transition-all duration-300 ${isAdminPanelCollapsed ? 'right-[620px] z-20' : 'right-[620px] z-[60]'}`}>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setIsAdminPanelCollapsed(!isAdminPanelCollapsed)}
                    className="px-2 py-1 h-8 bg-primary/20 hover:bg-primary/30 text-primary border-primary/50"
                  >
                    {isAdminPanelCollapsed ? '⚙️' : '✕'}
                  </Button>
                  {!isAdminPanelCollapsed && (
                    <MapAdminPanel
                      selectedHospital={selectedHospital}
                      isAdmin={adminStatus.isAdmin}
                      onHospitalUpdate={(updatedHospital) => { setSelectedHospital(updatedHospital); fetchHospitals(); }}
                    />
                  )}
                </div>
              </div>
            )}

            {!mapLoaded && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-sm text-muted-foreground">{t('loading')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Panel */}
          <MapBottomPanel
            selectedHospital={selectedHospital}
            isProceduresExpanded={isProceduresExpanded} setIsProceduresExpanded={setIsProceduresExpanded}
            procedureCategory={procedureCategory} setProcedureCategory={setProcedureCategory}
            priceFilter={priceFilter} setPriceFilter={setPriceFilter}
            debugToggles={debugToggles}
            activeScenarioId={activeScenarioId}
            scenarioInfo={scenarioInfo}
          />
        </div>

        {/* Right Panel */}
        <MapRightPanel
          isAhp={isAhp} panelBgGradient={panelBgGradient} panelCardBg={panelCardBg} accentColor={accentColor}
          debugToggles={debugToggles}
          selectedHospital={selectedHospital} setSelectedHospital={setSelectedHospital}
          hospitals={hospitals} hospitalStats={hospitalStats}
          hospitalEarnings={hospitalEarnings} isLoadingEarnings={isLoadingEarnings} isDataReady={isDataReady}
          navigate={navigate} flyToState={flyToState} flyToCity={flyToCity}
          selectedTableState={selectedTableState} setSelectedTableState={setSelectedTableState}
          selectedTableCity={selectedTableCity} setSelectedTableCity={setSelectedTableCity}
          activeScenarioId={activeScenarioId} setActiveScenarioId={setActiveScenarioId}
          scenarioInfo={scenarioInfo} setSearchParams={setSearchParams}
          scenarioHospitalPricing={scenarioHospitalPricing}
          fetchHospitalProcedures={fetchHospitalProcedures} orbitHospital={focusHospital}
        />
      </div>

      {/* Debug Panel */}
      <MapDebugPanel
        debugToggles={debugToggles} setDebugToggles={setDebugToggles}
        showDebugPanel={showDebugPanel} setShowDebugPanel={setShowDebugPanel}
        hospitalCount={hospitals.length} entityCount={viewerRef.current?.entities?.values?.length || 0}
      />

      {/* Watermark */}
      <style>{`
        @keyframes borderPulse { 0%, 100% { border-color: #00ff0380; } 50% { border-color: #ff000080; } }
        @keyframes borderPulseAntiGreed { 0%, 100% { border-color: #1e5f4b80; } 50% { border-color: #ff660080; } }
      `}</style>
      {debugToggles.watermark && (brand.key === 'antigreed' ? (
        <img src={brand.appIconUrl} alt="Anti-Greed Guide"
          style={{ zIndex: 100000, position: 'absolute', width: '400px', bottom: '72px', opacity: 0.2, borderRadius: '75px', border: 'solid #1e5f4b80 3px', animation: 'borderPulseAntiGreed 3s ease-in-out infinite' }} />
      ) : brand.key === 'ahp' ? null : (
        <img src={brand.appIconUrl} alt={`${brand.name} Logo`}
          style={{ zIndex: 100000, position: 'absolute', width: '400px', bottom: '72px', opacity: 0.2, borderRadius: '75px', border: 'solid #00ff0380 3px', animation: 'borderPulse 3s ease-in-out infinite' }} />
      ))}

      {/* Matrix Loader */}
      <MatrixLoader isLoading={showMatrixLoader} />
    </div>
  );
};

export default HospitalMap;
